"use client";

import type { MouseEvent } from "react";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { Mail, MapPin, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";

import { CONTACT_INFO, CONTACT_LINKS } from "@/shared/config/contact";

function smoothScroll(e: MouseEvent<HTMLAnchorElement>) {
  const href = e.currentTarget.getAttribute("href");
  if (!href?.startsWith("#")) return;
  const el = document.getElementById(href.slice(1));
  if (el) {
    e.preventDefault();
    el.scrollIntoView({ behavior: "smooth" });
  }
}

export function LandingFooter() {
  const { t } = useTranslation();
  const pathname = usePathname();
  const localePrefix = pathname.startsWith("/ru") ? "/ru" : pathname.startsWith("/en") ? "/en" : "";

  const quickLinks = [
    { href: "#features", label: t("landing.nav.platform") },
    { href: `${localePrefix}/how-it-works`, label: t("landing.hero.ctaSecondary"), external: true },
    { href: "/privacy", label: t("landing.footer.privacy"), external: true },
    { href: "/account-deletion", label: t("landing.footer.accountDeletion"), external: true },
    { href: "#activity", label: t("landing.nav.activity") },
    { href: "/auth/login", label: t("landing.nav.login"), external: true },
  ];

  const contacts = [
    { icon: Mail, label: CONTACT_INFO.email, href: CONTACT_LINKS.email },
    { icon: Phone, label: CONTACT_INFO.phone, href: CONTACT_LINKS.phone },
    { icon: MapPin, label: CONTACT_INFO.address, href: null },
  ];

  return (
    <footer className="border-t border-slate-100 bg-white">
      <div className="mx-auto max-w-7xl px-6 py-12">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex flex-col gap-3">
            <Link href="/" className="flex items-center gap-2">
              <Image src="/images/logo.svg" alt="PharmaLogic" width={32} height={32} className="size-8 rounded-md" />
              <span className="text-base font-semibold text-slate-900">PharmaLogic</span>
            </Link>
            <p className="max-w-xs text-sm text-slate-500">{t("landing.footer.tagline")}</p>
          </div>

          <div className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold tracking-wide text-slate-900 uppercase">
              {t("landing.footer.quickLinks")}
            </h3>
            <ul className="flex flex-col gap-2">
              {quickLinks.map((link) =>
                link.external ? (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="inline-flex min-h-11 items-center py-2 text-sm text-slate-500 transition-colors hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-teal-500/40 focus-visible:outline-none sm:min-h-0 sm:py-0"
                    >
                      {link.label}
                    </Link>
                  </li>
                ) : (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      onClick={smoothScroll}
                      className="inline-flex min-h-11 items-center py-2 text-sm text-slate-500 transition-colors hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-teal-500/40 focus-visible:outline-none sm:min-h-0 sm:py-0"
                    >
                      {link.label}
                    </a>
                  </li>
                ),
              )}
            </ul>
          </div>

          <div className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold tracking-wide text-slate-900 uppercase">
              {t("landing.footer.contact")}
            </h3>
            <ul className="flex flex-col gap-2">
              {contacts.map((item) => {
                const Icon = item.icon;
                const content = (
                  <span className="flex min-h-11 items-center gap-2 py-2 text-sm text-slate-500 transition-colors hover:text-slate-900 sm:min-h-0 sm:py-0">
                    <Icon className="size-4 text-slate-400" />
                    {item.label}
                  </span>
                );
                return (
                  <li key={item.label}>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="inline-flex focus-visible:ring-2 focus-visible:ring-teal-500/40 focus-visible:outline-none"
                      >
                        {content}
                      </a>
                    ) : (
                      content
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-100">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-6 py-5 sm:flex-row">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <p>
              &copy; {new Date().getFullYear()} PharmaLogic. {t("landing.footer.rights")}
            </p>
          </div>
          <p className="text-xs font-medium text-slate-500">
            {t("landing.footer.poweredBy")}{" "}
            <a
              href="https://banerma.uz"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-11 items-center font-semibold text-teal-600 transition-colors hover:text-teal-700 focus-visible:ring-2 focus-visible:ring-teal-500/40 focus-visible:outline-none sm:min-h-0"
            >
              Banerma
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
